import { CaseIcon } from 'assets/icons';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import { FormControl } from 'pages/ModerationRequest/redesign/FormControl';
import { FormWrapper } from 'pages/ModerationRequest/redesign/FormWrapper';
import { FieldValues, SubmitHandler, useForm, UseFormWatch } from 'react-hook-form';
import { useQuery } from 'react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { moderationRequestActions } from 'reduxStore/reducers/ModerationRequestSlice';
import { getModerationRequestData } from 'reduxStore/reducers/ModerationRequestSlice/selectors/getModerationRequestData';
import { getModerationRequestStatus } from 'reduxStore/reducers/ModerationRequestSlice/selectors/getModerationRequestStatus';
import { getProfile } from 'reduxStore/reducers/ProfileSlice/selectors/getProfile';
import { ProfileService } from 'services/ProfileService';
import { Step4Fields } from './Step4Fields';
import { Step4Payload } from './types';
import { ModerationRequestForm } from '../finalStep/types';
import { getIsUserAgentFromInvitation } from 'reduxStore/reducers/ProfileSlice/selectors/getIsUserAgentFromInvitation';
import { EExecutorSpecialization } from 'enums/executorSpecialization';

export const Step4 = () => {
  const {
    status,
    primaryCity,
    serviceCities,
    executorSpecializations,
    acceptsOffer,
    parentId,
    executorType,
  } = useAppSelector(getModerationRequestData);
  const isUserAgentFromInvitation = useAppSelector(getIsUserAgentFromInvitation);

  const isFormDisabled = useAppSelector(getModerationRequestStatus);
  const { created } = useAppSelector(getProfile);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const step = pathname.split('step')[1];

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    clearErrors,
    watch,
    getValues,
  } = useForm<FieldValues>({
    defaultValues: {
      primaryCity: primaryCity ? { label: primaryCity?.objectFullName, value: primaryCity } : null,
      serviceCities: serviceCities?.map((item: any) => ({
        label: item.objectFullName,
        value: item,
      })),
      executorSpecializations: isUserAgentFromInvitation
        ? [EExecutorSpecialization.Agent]
        : executorSpecializations,
      acceptsOffer,
    },
    mode: 'onBlur',
  });

  useQuery(['getUserCommonSettlements'], () => ProfileService.getUserCommonSettlementsV2(), {
    onSuccess: (data) => {
      data.data.primaryCommonSettlement &&
        setValue('primaryCity', {
          label: data.data.primaryCommonSettlement.objectFullName,
          value: data.data.primaryCommonSettlement,
        });
      if (
        data.data.additionalCommonSettlements &&
        data.data.additionalCommonSettlements.length > 0
      ) {
        setValue(
          'serviceCities',
          data.data.additionalCommonSettlements.map((item) => ({
            label: item.objectFullName,
            value: item,
          }))
        );
      }
    },
  });

  const onSubmit: SubmitHandler<any> = (data, event) => {
    const extracted = data?.serviceCities?.map((item: any) => {
      if ('isMarked' in item.value) {
        return { fiasId: item.value.suggestion.data.fias_id, objectFullName: item.label };
      }
      return item.value;
    });

    const primaryCityExtracted = data?.primaryCity?.value?.fiasId
      ? {
          fiasId: data?.primaryCity?.value?.fiasId,
          objectFullName: data?.primaryCity?.value?.objectFullName,
        }
      : null;

    const newData = { ...data, primaryCity: primaryCityExtracted, serviceCities: extracted };

    dispatch(moderationRequestActions.setSettlementsAndSpecializations(newData as Step4Payload));
    const nativeEvent = event?.nativeEvent as SubmitEvent;
    const submitter = nativeEvent.submitter as HTMLButtonElement;
    if (submitter.name === 'forward') {
      const nextStep = +step + 1;
      navigate(`/moderation-request/step${nextStep}`);
    } else if (submitter.name === 'back') {
      const prevStep = +step - 1;
      prevStep
        ? prevStep && navigate(`/moderation-request/step${prevStep}`)
        : navigate(`/moderation-request`);
    }
  };

  return (
    <FormWrapper
      title="Специализации и территория работы"
      status={status}
      created={created}
      Icon={CaseIcon}
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex min-h-[65vh] flex-col justify-between"
      >
        <Step4Fields
          clearErrors={clearErrors}
          errors={errors}
          parentId={parentId}
          control={control}
          watch={watch}
          isFormDisabled={isFormDisabled}
          setValue={setValue}
          executorType={executorType}
          getValues={getValues}
          isUserAgentFromInvitation={isUserAgentFromInvitation}
        />
        <FormControl
          errors={errors}
          watch={watch as unknown as UseFormWatch<ModerationRequestForm>}
        />
      </form>
    </FormWrapper>
  );
};
